'use client'
import { Box, Group, Skeleton } from '@mantine/core'
import { useTranslations } from 'next-intl'
import React, { Suspense, useCallback, useMemo, useState } from 'react'

import { SectionErrorBoundary } from '@/components/error/boundary'
import Swiper from '@/components/Swiper'
import useSubscribeLiveCoinsMarket from '@/hooks/business/useSubscribeLiveCoinsMarket'
import type { CoinItemInGetList } from '@/services/coin/coins'
import { useCoins, useTopMovers } from '@/services/coin/coins'
import type { BuzzingOnXItem, HotCoinsData } from '@/services/coin/market'
import { TrendingCardType } from '@/services/coin/market'
import type { CoinSlug, LiveCoinMarket, TopMoverItem } from '@/types/coin'

import TrendingCard from './TrendingCard'

function TrendingFallback() {
  return (
    <Box p={16}>
      <Skeleton
        height={32}
        w={160}
        radius="xl"
        mb="sm"
        style={{ backgroundColor: 'var(--bg-surface-secondary)' }}
      />
      <Group gap="md" wrap="nowrap">
        {[0, 1, 2, 3].map(index => (
          <Skeleton
            key={index}
            h={{ base: 270, md: 274 }}
            w={{ base: 253, md: 230, lg: 302 }}
            miw={{ base: 253, md: 230, lg: 302 }}
            style={{
              backgroundColor: 'var(--bg-surface-secondary)',
              flexShrink: 0,
              borderRadius: 32,
            }}
          />
        ))}
      </Group>
    </Box>
  )
}

interface TrendingContentProps {
  buzzingOnXData?: BuzzingOnXItem[]
  hotCoinsData?: HotCoinsData
  newCoinsData?: CoinItemInGetList[]
  topMoversData?: TopMoverItem[]
}

function TrendingContent({
  buzzingOnXData,
  hotCoinsData,
  newCoinsData,
  topMoversData,
  liveCache,
}: TrendingContentProps & { liveCache: Record<CoinSlug, LiveCoinMarket> }) {
  const t = useTranslations('trending')

  const swiperList = useMemo(() => {
    return [
      { data: hotCoinsData, type: TrendingCardType.HotTokens },
      { data: buzzingOnXData, type: TrendingCardType.BuzzingX },
      { data: topMoversData, type: TrendingCardType.TopMovers },
      { data: newCoinsData, type: TrendingCardType.NewlyListed },
    ]
  }, [buzzingOnXData, topMoversData, hotCoinsData, newCoinsData])

  if (!swiperList || swiperList.length === 0) return null

  return (
    <>
      <Swiper
        title={t('title_2')}
        swiperList={swiperList}
        renderSwiper={(item, idx) => (
          <TrendingCard type={item.type} key={idx} data={item.data} liveCache={liveCache} />
        )}
      />
    </>
  )
}

interface TrendingProps {
  buzzingOnXData?: BuzzingOnXItem[]
  hotCoinsData?: HotCoinsData
}

function Trending({ buzzingOnXData, hotCoinsData }: TrendingProps) {
  const { data: topMoversData } = useTopMovers({ depth: 20 })
  const { data: newCoins } = useCoins({
    page: 1,
    limit: 100,
    sort: 'createTime',
    direction: 'DESC',
  })
  const newCoinsData = newCoins?.data || []

  // Top-level subscription for all trending slugs
  const [liveCache, setLiveCache] = useState<Record<CoinSlug, LiveCoinMarket>>({})
  const updateLiveCoinsMarket = useCallback((next: Record<CoinSlug, LiveCoinMarket>) => {
    setLiveCache(next)
  }, [])
  const clearLiveCoinsMarket = useCallback(() => {
    setLiveCache({})
  }, [])
  const combinedSlugs = useMemo(() => {
    const slugs = new Set<CoinSlug>()
    hotCoinsData?.forEach(c => c?.slug && slugs.add(c.slug as CoinSlug))
    topMoversData?.forEach(c => c?.slug && slugs.add(c.slug as CoinSlug))
    newCoinsData?.forEach(c => c?.slug && slugs.add(c.slug as CoinSlug))
    buzzingOnXData?.forEach(c => c?.name && slugs.add(c.name.toLowerCase() as CoinSlug))
    return Array.from(slugs)
  }, [hotCoinsData, topMoversData, newCoinsData, buzzingOnXData])
  useSubscribeLiveCoinsMarket(combinedSlugs, { updateLiveCoinsMarket, clearLiveCoinsMarket }, 1000)

  return (
    <Suspense fallback={<TrendingFallback />}>
      <TrendingContent
        buzzingOnXData={buzzingOnXData}
        topMoversData={topMoversData}
        hotCoinsData={hotCoinsData}
        newCoinsData={newCoinsData}
        liveCache={liveCache}
      />
      <Box id="trending-bottom"></Box>
    </Suspense>
  )
}

export default SectionErrorBoundary.with(Trending)
